import React, { useState, useRef, useEffect } from 'react';

const ColorPicker = ({
  value = '#8000ff',
  onChange = () => {},
  disabled = false,
  showAlpha = false,
  showPresets = true,
  showInput = true,
  showCopyButton = true,
  format = 'hex', // 'hex', 'rgb', 'hsl'
  enabledFormats = ['hex', 'rgb', 'hsl'], // Which formats to show in tabs
  presetColors = [],
  label = '',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentColor, setCurrentColor] = useState(value);
  const [inputValue, setInputValue] = useState(value);
  const [activeFormat, setActiveFormat] = useState(format);
  const [hue, setHue] = useState(0);
  const [saturation, setSaturation] = useState(100);
  const [lightness, setLightness] = useState(50);
  const [alpha, setAlpha] = useState(1);
  
  const colorPickerRef = useRef(null);
  const saturationRef = useRef(null);
  const hueRef = useRef(null);
  const alphaRef = useRef(null);

  // Default preset colors
  const defaultPresets = [
    '#FF0000', '#FF8000', '#FFFF00', '#80FF00', '#00FF00', '#00FF80',
    '#00FFFF', '#0080FF', '#0000FF', '#8000FF', '#FF00FF', '#FF0080',
    '#000000', '#404040', '#808080', '#C0C0C0', '#FFFFFF', '#F5F5DC'
  ];

  const presets = presetColors.length > 0 ? presetColors : defaultPresets;

  useEffect(() => {
    // Ensure the activeFormat is valid with enabled formats
    if (!enabledFormats.includes(activeFormat)) {
      setActiveFormat(enabledFormats[0] || 'hex');
    }
    
    const color = parseColor(value);
    updateFromColor(color);
    setCurrentColor(value);
    setInputValue(formatColor(color, enabledFormats.includes(activeFormat) ? activeFormat : enabledFormats[0]));
  }, [value, enabledFormats]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const hslToRgb = (h, s, l) => {
    h /= 360;
    s /= 100;
    l /= 100;

    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    if (s === 0) {
      const gray = Math.round(l * 255);
      return { r: gray, g: gray, b: gray };
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    return {
      r: Math.round(hue2rgb(p, q, h + 1/3) * 255),
      g: Math.round(hue2rgb(p, q, h) * 255),
      b: Math.round(hue2rgb(p, q, h - 1/3) * 255)
    };
  };

  const rgbToHex = (r, g, b) => {
    return '#' + [r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('');
  };

  const parseColor = (colorStr) => {
    if (colorStr.startsWith('#')) {
      const rgb = hexToRgb(colorStr);
      return rgb ? { ...rgb, a: 1 } : { r: 128, g: 0, b: 255, a: 1 };
    }
    return { r: 128, g: 0, b: 255, a: 1 };
  };

  const formatColor = (color, fmt) => {
    switch (fmt) {
      case 'rgb':
        return showAlpha 
          ? `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`
          : `rgb(${color.r}, ${color.g}, ${color.b})`;
      case 'hsl':
        const hsl = rgbToHsl(color.r, color.g, color.b);
        return showAlpha
          ? `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, ${alpha})`
          : `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
      default:
        return rgbToHex(color.r, color.g, color.b);
    }
  };

  const updateFromColor = (color) => {
    const hsl = rgbToHsl(color.r, color.g, color.b);
    setHue(hsl.h);
    setSaturation(hsl.s);
    setLightness(hsl.l);
    setAlpha(color.a || 1);
  };

  const updateColor = (h, s, l, a = alpha) => {
    const rgb = hslToRgb(h, s, l);
    const color = { ...rgb, a };
    const formattedColor = formatColor(color, activeFormat);
    
    setCurrentColor(formattedColor);
    setInputValue(formattedColor);
    onChange(formattedColor);
  };

  const handleSaturationMouseDown = (e) => {
    if (disabled) return;
    
    const updateSaturation = (e) => {
      const rect = saturationRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
      
      const newSaturation = x * 100;
      const newLightness = (1 - y) * 100;
      
      setSaturation(newSaturation);
      setLightness(newLightness);
      updateColor(hue, newSaturation, newLightness);
    };

    const handleMouseMove = (e) => updateSaturation(e);
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    updateSaturation(e);
  };

  const handleHueChange = (e) => {
    const newHue = parseInt(e.target.value);
    setHue(newHue);
    updateColor(newHue, saturation, lightness);
  };

  const handleAlphaChange = (e) => {
    const newAlpha = parseFloat(e.target.value);
    setAlpha(newAlpha);
    updateColor(hue, saturation, lightness, newAlpha);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    try {
      const color = parseColor(value);
      updateFromColor(color);
      setCurrentColor(value);
      onChange(value);
    } catch (error) {
      // Invalid color format
    }
  };

  const handlePresetClick = (color) => {
    const parsedColor = parseColor(color);
    updateFromColor(parsedColor);
    const formattedColor = formatColor(parsedColor, activeFormat);
    setCurrentColor(formattedColor);
    setInputValue(formattedColor);
    onChange(formattedColor);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(currentColor);
  };

  return (
    <>
      <link 
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" 
        rel="stylesheet"
      />
      
      <style>{`
        .color-picker-container {
          width: 100%;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          position: relative;
        }

        .color-picker-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: #374151;
          font-size: 14px;
        }

        .color-picker-trigger {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border: 2px solid #d1d5db;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .color-picker-trigger:hover {
          border-color: #9ca3af;
        }

        .color-picker-trigger.disabled {
          cursor: not-allowed;
          opacity: 0.5;
        }

        .color-swatch {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
          flex-shrink: 0;
        }

        .color-value {
          flex: 1;
          font-family: monospace;
          font-size: 14px;
          color: #374151;
        }

        .color-picker-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 1000;
          margin-top: 4px;
          padding: 16px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          width: 320px;
          max-width: 100vw;
        }

        .saturation-area {
          width: 100%;
          height: 150px;
          position: relative;
          margin-bottom: 16px;
          border-radius: 8px;
          cursor: crosshair;
          background: linear-gradient(to right, white, hsl(var(--hue), 100%, 50%)),
                      linear-gradient(to top, black, transparent);
        }

        .saturation-pointer {
          position: absolute;
          width: 12px;
          height: 12px;
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 0 3px rgba(0, 0, 0, 0.3);
          transform: translate(-50%, -50%);
          pointer-events: none;
        }

        .hue-slider {
          width: 100%;
          height: 12px;
          margin-bottom: 12px;
          border-radius: 6px;
          background: linear-gradient(to right, 
            #ff0000 0%, #ffff00 16.66%, #00ff00 33.33%, 
            #00ffff 50%, #0000ff 66.66%, #ff00ff 83.33%, #ff0000 100%);
          -webkit-appearance: none;
          appearance: none;
          outline: none;
        }

        .hue-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          border: 2px solid #ccc;
          cursor: pointer;
        }

        .alpha-slider {
          width: 100%;
          height: 12px;
          margin-bottom: 16px;
          border-radius: 6px;
          background: linear-gradient(45deg, #ccc 25%, transparent 25%),
                      linear-gradient(-45deg, #ccc 25%, transparent 25%),
                      linear-gradient(45deg, transparent 75%, #ccc 75%),
                      linear-gradient(-45deg, transparent 75%, #ccc 75%);
          background-size: 8px 8px;
          background-position: 0 0, 0 4px, 4px -4px, -4px 0px;
          -webkit-appearance: none;
          appearance: none;
          outline: none;
        }

        .alpha-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: white;
          border: 2px solid #ccc;
          cursor: pointer;
        }

        .format-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: 12px;
        }

        .format-tab {
          flex: 1;
          padding: 6px 12px;
          border: 1px solid #d1d5db;
          background: white;
          border-radius: 6px;
          font-size: 12px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .format-tab.active {
          background: #3b82f6;
          color: white;
          border-color: #3b82f6;
        }

        .input-row {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        .color-input {
          flex: 1;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-family: monospace;
          font-size: 14px;
          min-width: 0;
        }

        .copy-button {
          padding: 8px 12px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .copy-button:hover {
          background: #e5e7eb;
        }

        .presets-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 8px;
        }

        .preset-color {
          width: 100%;
          height: 32px;
          border-radius: 6px;
          border: 2px solid #e5e7eb;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .preset-color:hover {
          transform: scale(1.05);
          border-color: #9ca3af;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .color-picker-dropdown {
            width: 300px;
          }
          
          .presets-grid {
            grid-template-columns: repeat(5, 1fr);
          }
          
          .saturation-area {
            height: 120px;
          }
        }

        @media (max-width: 576px) {
          .color-picker-dropdown {
            width: 280px;
            left: 0;
            right: 0;
          }
          
          .presets-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
          }
          
          .preset-color {
            height: 28px;
          }
          
          .saturation-area {
            height: 100px;
          }
        }
      `}</style>

      <div className={`color-picker-container ${className}`} ref={colorPickerRef}>
        {label && (
          <label className="color-picker-label">{label}</label>
        )}
        
        <div 
          className={`color-picker-trigger ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div 
            className="color-swatch"
            style={{ backgroundColor: currentColor }}
          />
          <span className="color-value">{currentColor}</span>
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#9ca3af' }}>
            {isOpen ? '▲' : '▼'}
          </span>
        </div>

        {isOpen && (
          <div className="color-picker-dropdown">
            {/* Format Tabs - Only show if more than one format is enabled */}
            {enabledFormats.length > 1 && (
              <div className="format-tabs">
                {enabledFormats.map(fmt => (
                  <button
                    key={fmt}
                    className={`format-tab ${activeFormat === fmt ? 'active' : ''}`}
                    onClick={() => {
                      setActiveFormat(fmt);
                      const color = parseColor(currentColor);
                      const formatted = formatColor(color, fmt);
                      setInputValue(formatted);
                      setCurrentColor(formatted);
                      onChange(formatted);
                    }}
                  >
                    {fmt.toUpperCase()}
                  </button>
                ))}
              </div>
            )}

            {/* Saturation/Lightness Area */}
            <div 
              className="saturation-area"
              style={{ '--hue': hue }}
              ref={saturationRef}
              onMouseDown={handleSaturationMouseDown}
            >
              <div 
                className="saturation-pointer"
                style={{
                  left: `${saturation}%`,
                  top: `${100 - lightness}%`
                }}
              />
            </div>

            {/* Hue Slider */}
            <input
              type="range"
              min="0"
              max="360"
              value={hue}
              onChange={handleHueChange}
              className="hue-slider"
              ref={hueRef}
            />

            {/* Alpha Slider */}
            {showAlpha && (
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={alpha}
                onChange={handleAlphaChange}
                className="alpha-slider"
                ref={alphaRef}
                style={{
                  background: `linear-gradient(to right, 
                    transparent, ${rgbToHex(...Object.values(hslToRgb(hue, saturation, lightness)))}),
                    linear-gradient(45deg, #ccc 25%, transparent 25%),
                    linear-gradient(-45deg, #ccc 25%, transparent 25%)`
                }}
              />
            )}

            {/* Input Row */}
            {showInput && (
              <div className="input-row">
                <input
                  type="text"
                  value={inputValue}
                  onChange={handleInputChange}
                  className="color-input"
                  placeholder="Enter color value"
                />
                {showCopyButton && (
                  <button className="copy-button" onClick={copyToClipboard}>
                    Copy
                  </button>
                )}
              </div>
            )}

            {/* Preset Colors */}
            {showPresets && (
              <div className="presets-grid">
                {presets.map((color, index) => (
                  <div
                    key={index}
                    className="preset-color"
                    style={{ backgroundColor: color }}
                    onClick={() => handlePresetClick(color)}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default ColorPicker;